<script lang="ts">
	import { slide } from 'svelte/transition'
	import bv from 'shared/object/body_validator'
	import { storefront_uber_fetch } from 'shared/ops_api'
	import type { SalesKitWithAvailability } from 'endpoint/sales_kit/sales_kit'
	import fetchy from 'shared/fetchy'
	import { storefront_prefix } from 'shared/var'
	import Button from 'component/Button.svelte'
	import ErrorMessage from 'component/ErrorMessage.svelte'
	import fnum, { financial_sum } from 'shared/number'
	import { format_money_$_with_optional_cents } from 'shared/format_currency'

	export let sales_kits: { sales_kit_name?: string, sales_kit_ref: string }[]

	let errors: string[] = []
	const validator = bv.array(bv.object({
		sales_kit_name: bv.optional(bv.string),
		sales_kit_ref: bv.integer,
	}))

	// barnacle initialization should always attempt converting data attributes to JSON.  if it's not an object, it's probably not valid JSON
	if (typeof sales_kits === `string`) {
		try {
			sales_kits = JSON.parse(sales_kits)
		} catch (e: any) {
			errors = [ e.toString() ]
			sales_kits = []
		}
	}

	if (!validator.is_valid(sales_kits)) {
		errors.push(...validator.get_messages(sales_kits, `sales_kits`))
	}

	let add_sales_kit_promises: Record<string, Promise<any>> | undefined = {}

	const sales_kit_refs = sales_kits.map(sales_kit => parseInt(sales_kit.sales_kit_ref))

	const query_string = sales_kit_refs
		.map(ref => `sales_kit_refs=${ encodeURIComponent(ref.toString()) }`)
		.join(`&`)

	const get_sales_kits_promise = (storefront_uber_fetch(`sales_kit/not_jsonapi/?${ query_string }`) as Promise<SalesKitWithAvailability[]>)
		.then(server_sales_kits => server_sales_kits.map(({ sales_kit_ref, name, sales_kit_items }) => ({
			sales_kit_ref,
			sales_kit_items,
			name: sales_kits.find(kit => Number(kit.sales_kit_ref) === sales_kit_ref)?.sales_kit_name || name,
			all_available: sales_kit_items.every(item => Number(item.available_quantity) >= item.quantity),
			summed_og_price: financial_sum(sales_kit_items.map(kit_item => fnum(kit_item.original_price?.toString() || `0`).times(kit_item.quantity.toString()))),
			sales_kit_price: financial_sum(sales_kit_items.map(item => fnum(item.price.toString()).times(item.quantity.toString()))),
		})))
		.catch(err => {
			errors.push(err.toString())
			return []
		})

	const add_sales_kit_fetch = fetchy(storefront_prefix)

	const nonce_promise = fetch(`/wp-admin/admin-ajax.php?action=rest-nonce`)
		.then(res => res.text())
		.then(nonce => nonce.trim())

	const add_sales_kit_to_cart = async(sales_kit_ref: number) => {
		const nonce = await nonce_promise

		add_sales_kit_promises[sales_kit_ref] = add_sales_kit_fetch(`/wp-json/cart/sales_kit/add${ nonce !== `0` ? `?_wpnonce=${ nonce }` : `` }`, {
			method: `POST`,
			body: {
				sales_kit_ref,
			},
		})
	}
</script>

{#each errors as message}
	<ErrorMessage {message} />
{/each}

{#await get_sales_kits_promise then kits}
	<div class=container>
		<table>
			<tbody>
				{#each kits.sort((a, b) => sales_kit_refs.indexOf(a.sales_kit_ref) - sales_kit_refs.indexOf(b.sales_kit_ref)) as kit (kit.sales_kit_ref)}
					{#if add_sales_kit_promises[kit.sales_kit_ref] !== undefined}
						{#await add_sales_kit_promises[kit.sales_kit_ref] then}
							<tr>
								<td colspan=3>
									<div transition:slide|global>
										{kit.name} added to cart.
										<a href="/cart">View Cart</a>
									</div>
								</td>
							</tr>
						{:catch server_error_response}
							<tr>
								<td colspan=3>
									<div transition:slide|global>
										<ErrorMessage {server_error_response} />
									</div>
								</td>
							</tr>
						{/await}
					{/if}
					<tr>
						<th>
							{kit.name}
						</th>
						<td>
							<div class=prices>
								<span class=normal-price>{format_money_$_with_optional_cents(kit.summed_og_price)}</span>
								<span class=sales-kit-price>{format_money_$_with_optional_cents(kit.sales_kit_price)}</span>
							</div>
						</td>
						<td>
							<div class="button-container">
								<Button
									disabled={!kit.all_available}
									on:click={() => add_sales_kit_to_cart(kit.sales_kit_ref)}
								>
									{kit.all_available ? `Add to cart` : `Out of stock`}
								</Button>
							</div>
						</td>
					</tr>
				{/each}
			</tbody>
		</table>
	</div>
{/await}

<style>
	:global(sales_kit_add_to_cart) {
		display: table;
	}

	.container {
		border-radius: 8px;
		border: 1px solid #444;
	}

	table {
		border-collapse: collapse;
		margin: 0;
	}

	td, th {
		border: 0 !important; /* beat woocommerce */
		border-bottom: solid 1px #444 !important; /* beat the beating of woocommerce */
		vertical-align: middle;
		padding: 5px;
	}

	tr:last-child td, tr:last-child th {
		border: 0 !important; /* beat the beating of the beating of woocommerce */
	}

	th {
		padding: 5px 10px;
		font-weight: 600;
		font-size: 110%;
		color: #ddd;
	}

	.prices {
		display: flex;
		flex-direction: column;
		line-height: 1.2;
	}

	.normal-price {
		font-weight: 600;
		color: grey;
		text-decoration: line-through;
	}

	.sales-kit-price {
		font-size: 120%;
		color: white;
	}
</style>
